import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useLocation, useMatch, useNavigate} from 'react-router-dom'

import {useErrorHandler} from '../../../../../../_metronic/helpers/ErrorHandler'
import {useMapLayersV2} from '../../../Map/MapLayersV2Context'
import {useViewFieldContext} from '../ViewFieldContext'
import * as GisDataAdapter from '../../services/GisDataAdapter'
import {drawState} from '../../../Map/Draw/DrawState'
import {isLandViewPathActive, resolveLandSnapshot} from './ViewLandRecovery'
import {
  DEFAULT_LAND_SECTION,
  getLastLandSection,
  hasLastLandSection,
  normalizeLandSection,
  setLastLandSection,
} from './landViewSectionState'

const ViewLandContext = createContext()

export {getLastLandSection, normalizeLandSection}

export const buildLandViewPath = (fieldId, landId, section = getLastLandSection(landId)) => {
  if (!fieldId || !landId) {
    return '/gis'
  }

  const safeSection = normalizeLandSection(section)
  const basePath = `/gis/field/view/${fieldId}/land/view/${landId}`

  if (safeSection === 'ambients') {
    return `${basePath}/ambients`
  }

  if (safeSection === 'layers') {
    return `${basePath}/layers`
  }

  if (safeSection === 'comparison') {
    return `${basePath}/comparison`
  }

  return `${basePath}?section=${DEFAULT_LAND_SECTION}`
}

export function useViewLandContext() {
  return useContext(ViewLandContext)
}

export const ViewLandConsumer = ViewLandContext.Consumer

export function ViewLandProvider({children}) {
  const [loading, setLoading] = useState(true)
  const [land, setLand] = useState(null)
  const [landId, setLandId] = useState(null)
  const [, setLandSectionStorageRevision] = useState(0)
  const {errorHandler} = useErrorHandler()
  const [mapLoaded, setMapLoaded] = useState(false)
  const currentLand = useRef(null)
  const loadedLandRef = useRef(null)
  const landIdRef = useRef(null)
  const loadRequestId = useRef(0)
  const landReadRef = useRef(null)
  const blockedLandReadsRef = useRef(new Set())
  const viewFieldContext = useViewFieldContext()
  const viewFieldContextRef = useRef(viewFieldContext)
  const errorHandlerRef = useRef(errorHandler)
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  const location = useLocation()
  const mapLayersV2Context = useMapLayersV2()
  const ambientsRoute = useMatch('/gis/field/view/:field_id/land/view/:land_id/ambients')
  const layersRoute = useMatch('/gis/field/view/:field_id/land/view/:land_id/layers')
  const comparisonRoute = useMatch('/gis/field/view/:field_id/land/view/:land_id/comparison')
  const updateRoute = useMatch('/gis/field/view/:field_id/land/view/:land_id/update')
  const baseViewRoute = useMatch('/gis/field/view/:field_id/land/view/:land_id')
  const currentRouteParams =
    ambientsRoute?.params ||
    layersRoute?.params ||
    comparisonRoute?.params ||
    updateRoute?.params ||
    baseViewRoute?.params ||
    {}
  const currentRouteLandIdRef = useRef(currentRouteParams.land_id || null)

  loadedLandRef.current = land
  landIdRef.current = landId
  viewFieldContextRef.current = viewFieldContext
  errorHandlerRef.current = errorHandler
  currentRouteLandIdRef.current = currentRouteParams.land_id || null
  navigateRef.current = navigate

  const initViewLand = (map) => {
    setMapLoaded(true)

    map.on('click', 'land', (e) => {
      if (drawState.isDrawing()) {
        return
      }

      const currentLand = e.features[0].properties

      if (blockedLandReadsRef.current.has(currentLand.land_id)) {
        return
      }

      if (currentLand.opacity != 0) {
        navigate(buildLandViewPath(
          currentLand.field_id,
          currentLand.land_id
        ))
      }
    })

    map.on('mouseenter', 'land', () => {
      if (drawState.isDrawing()) {
        return
      }

      map.getCanvas().style.cursor = 'pointer'
    })

    map.on('mouseleave', 'land', () => {
      if (drawState.isDrawing()) {
        return
      }

      map.getCanvas().style.cursor = 'unset'
    })
  }

  const startView = useCallback(
    (id) => {
      mapLayersV2Context.fit(id)
      mapLayersV2Context.land.unfill(id)
      mapLayersV2Context.land.marker.hide(id)

      if (id != currentLand.current && currentLand.current != null) {
        mapLayersV2Context.land.fill(currentLand.current)
        mapLayersV2Context.land.marker.show(currentLand.current)
      }

      currentLand.current = id
    },
    [mapLayersV2Context]
  )

  const getAvailableLandSnapshot = useCallback((targetLandId) => {
    if (blockedLandReadsRef.current.has(targetLandId)) {
      return null
    }
    return resolveLandSnapshot({
      currentLand: loadedLandRef.current,
      targetLandId,
      field: viewFieldContextRef.current.field,
      lands: viewFieldContextRef.current.lands,
    })
  }, [])

  const loadLand = useCallback(
    async (targetLandId = landIdRef.current) => {
      if (!targetLandId || currentRouteLandIdRef.current !== targetLandId ||
          blockedLandReadsRef.current.has(targetLandId)) {
        return null
      }

      landReadRef.current?.controller.abort()
      const requestId = ++loadRequestId.current
      const request = {landId: targetLandId, controller: new AbortController()}
      landReadRef.current = request
      const isCurrent = () => requestId === loadRequestId.current &&
        landReadRef.current === request && currentRouteLandIdRef.current === targetLandId &&
        !blockedLandReadsRef.current.has(targetLandId)
      const currentSnapshot = getAvailableLandSnapshot(targetLandId)

      if (currentSnapshot) {
        loadedLandRef.current = currentSnapshot
        setLand(currentSnapshot)
        setLoading(false)
      } else {
        setLoading(true)
      }

      try {
        let res
        try {
          res = await GisDataAdapter.getLand(targetLandId, {signal: request.controller.signal})
        } catch (error) {
          // Check ownership before invoking the global handler: otherwise a
          // late 404 can toast even though its state update is ignored below.
          if (!isCurrent() || request.controller.signal.aborted) {
            return null
          }
          res = await errorHandlerRef.current(Promise.reject(error))
        }

        if (!isCurrent()) {
          return null
        }

        if (res?.ok && res.data) {
          loadedLandRef.current = res.data
          setLand(res.data)
          return res.data
        }

        const fallbackSnapshot = getAvailableLandSnapshot(targetLandId)

        if (fallbackSnapshot) {
          loadedLandRef.current = fallbackSnapshot
          setLand(fallbackSnapshot)
          return fallbackSnapshot
        }

        const fieldId = viewFieldContextRef.current.field?.field_id
        if (res?.error_type === 'not_found' && fieldId) {
          navigateRef.current(`/gis/field/view/${fieldId}`)
        }

        return null
      } finally {
        if (isCurrent()) {
          landReadRef.current = null
          setLoading(false)
        }
      }
    },
    [getAvailableLandSnapshot]
  )

  const invalidateLandRead = useCallback((targetLandId) => {
    if (landReadRef.current?.landId === targetLandId) {
      ++loadRequestId.current
      landReadRef.current.controller.abort()
      landReadRef.current = null
    }
  }, [])

  const suspendLandRequests = useCallback((targetLandId) => {
    blockedLandReadsRef.current.add(targetLandId)
    invalidateLandRead(targetLandId)
    if (landIdRef.current === targetLandId) setLoading(false)
  }, [invalidateLandRead])

  const resumeLandRequests = useCallback((targetLandId) => {
    if (!blockedLandReadsRef.current.delete(targetLandId)) return
    if (currentRouteLandIdRef.current === targetLandId) loadLand(targetLandId)
  }, [loadLand])

  const forgetDeletedLand = useCallback((targetLandId) => {
    suspendLandRequests(targetLandId)
    if (loadedLandRef.current?.land_id === targetLandId) loadedLandRef.current = null
    if (landIdRef.current === targetLandId) landIdRef.current = null
    if (currentLand.current === targetLandId) currentLand.current = null
    setLandId((previous) => previous === targetLandId ? null : previous)
    setLand((previous) => previous?.land_id === targetLandId ? null : previous)
  }, [suspendLandRequests])

  useEffect(() => () => {
    ++loadRequestId.current
    landReadRef.current?.controller.abort()
    landReadRef.current = null
  }, [])

  const stopViewingLand = useCallback(
    (currentLandId) => {
      const pathname = typeof window === 'undefined' ? '' : window.location.pathname

      if (isLandViewPathActive(pathname, currentLandId)) {
        setLoading(false)
        return
      }

      invalidateLandRead(currentLandId)
      if (loadedLandRef.current?.land_id === currentLandId) loadedLandRef.current = null
      if (landIdRef.current === currentLandId) landIdRef.current = null
      setLandId((previousLandId) =>
        previousLandId === currentLandId ? null : previousLandId
      )
      setLand((previousLand) =>
        previousLand?.land_id === currentLandId ? null : previousLand
      )
      if (!blockedLandReadsRef.current.has(currentLandId)) {
        mapLayersV2Context.land.fill(currentLandId)
        mapLayersV2Context.land.marker.show(currentLandId)
      }
    },
    [invalidateLandRead, mapLayersV2Context]
  )

  useEffect(() => {
    if (blockedLandReadsRef.current.has(landId)) {
      landIdRef.current = null
      setLandId(null)
      setLand(null)
      setLoading(false)
      return
    }
    if (landId != null) {
      const currentSnapshot = getAvailableLandSnapshot(landId)

      if (currentSnapshot) {
        loadedLandRef.current = currentSnapshot
        setLand(currentSnapshot)
        setLoading(false)
      } else {
        setLoading(true)
      }

      loadLand(landId)
      return
    }

    setLand(null)
    setLoading(false)
  }, [getAvailableLandSnapshot, landId, loadLand])

  useEffect(() => {
    if (!landId || blockedLandReadsRef.current.has(landId) || land?.land_id === landId) {
      return
    }

    const fallbackSnapshot = resolveLandSnapshot({
      currentLand: land,
      targetLandId: landId,
      field: viewFieldContext.field,
      lands: viewFieldContext.lands,
    })

    if (!fallbackSnapshot) {
      return
    }

    loadedLandRef.current = fallbackSnapshot
    setLand(fallbackSnapshot)
    setLoading(false)
  }, [land, landId, viewFieldContext.field, viewFieldContext.lands])

  useEffect(() => {
    const recoverVisibleLand = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return
      }

      const targetLandId = currentRouteLandIdRef.current || landIdRef.current

      if (!targetLandId || blockedLandReadsRef.current.has(targetLandId)) {
        return
      }

      const currentSnapshot = getAvailableLandSnapshot(targetLandId)

      if (currentSnapshot) {
        loadedLandRef.current = currentSnapshot
        setLand(currentSnapshot)
        setLoading(false)
        return
      }

      setLandId(targetLandId)
      loadLand(targetLandId)
    }

    window.addEventListener('pageshow', recoverVisibleLand)
    document.addEventListener('visibilitychange', recoverVisibleLand)

    return () => {
      window.removeEventListener('pageshow', recoverVisibleLand)
      document.removeEventListener('visibilitychange', recoverVisibleLand)
    }
  }, [getAvailableLandSnapshot, loadLand])

  useEffect(() => {
    if (landId == null || blockedLandReadsRef.current.has(landId) || land == null || mapLoaded == false) {
      return
    }

    startView(landId)
  }, [land, landId, mapLoaded])

  const currentFieldId =
    currentRouteParams.field_id || land?.field?.field_id || viewFieldContext.field?.field_id || null
  const routeLandId = currentRouteParams.land_id || null
  const currentLandId = routeLandId || land?.land_id || landId || null
  const isFirstDocumentLandVisit = Boolean(routeLandId && !hasLastLandSection(routeLandId))

  const baseLandPath = useMemo(() => {
    if (!currentFieldId || !currentLandId) {
      return null
    }

    return `/gis/field/view/${currentFieldId}/land/view/${currentLandId}`
  }, [currentFieldId, currentLandId])

  const fieldPath = useMemo(() => {
    if (!currentFieldId) {
      return null
    }

    return `/gis/field/view/${currentFieldId}`
  }, [currentFieldId])

  const requestedSection = useMemo(() => {
    return new URLSearchParams(location.search).get('section')
  }, [location.search])

  const activeSection = useMemo(() => {
    if (ambientsRoute != null) {
      return 'ambients'
    }

    if (layersRoute != null) {
      return 'layers'
    }

    if (comparisonRoute != null) {
      return 'comparison'
    }

    if (requestedSection === 'images') {
      return 'images'
    }

    if (requestedSection === 'general') {
      return 'images'
    }

    return 'images'
  }, [ambientsRoute, comparisonRoute, layersRoute, requestedSection])

  const getSectionHref = useCallback((section) => {
    if (!baseLandPath) {
      return '/gis'
    }

    const safeSection = normalizeLandSection(section)

    if (safeSection === 'ambients') {
      return `${baseLandPath}/ambients`
    }

    if (safeSection === 'layers') {
      return `${baseLandPath}/layers`
    }

    if (safeSection === 'comparison') {
      return `${baseLandPath}/comparison`
    }

    return `${baseLandPath}?section=${DEFAULT_LAND_SECTION}`
  }, [baseLandPath])

  useEffect(() => {
    if (!isFirstDocumentLandVisit || !currentFieldId || !currentLandId || updateRoute != null) {
      return
    }

    setLastLandSection(currentLandId, DEFAULT_LAND_SECTION)
    setLandSectionStorageRevision((revision) => revision + 1)

    if (baseViewRoute != null && requestedSection === DEFAULT_LAND_SECTION) {
      return
    }

    navigate(buildLandViewPath(currentFieldId, currentLandId, DEFAULT_LAND_SECTION), {
      replace: true,
    })
  }, [
    baseViewRoute,
    currentFieldId,
    currentLandId,
    isFirstDocumentLandVisit,
    navigate,
    requestedSection,
    updateRoute,
  ])

  useEffect(() => {
    if (
      isFirstDocumentLandVisit ||
      !routeLandId ||
      updateRoute != null ||
      (baseViewRoute != null && requestedSection === null)
    ) {
      return
    }

    setLastLandSection(routeLandId, activeSection)
  }, [
    activeSection,
    baseViewRoute,
    isFirstDocumentLandVisit,
    requestedSection,
    routeLandId,
    updateRoute,
  ])

  useEffect(() => {
    if (
      !baseViewRoute ||
      isFirstDocumentLandVisit ||
      requestedSection !== null ||
      !currentFieldId ||
      !currentLandId ||
      updateRoute != null
    ) {
      return
    }

    const storedSection = getLastLandSection(currentLandId)

    if (storedSection === DEFAULT_LAND_SECTION) {
      return
    }

    navigate(buildLandViewPath(currentFieldId, currentLandId, storedSection), {replace: true})
  }, [
    baseViewRoute,
    currentFieldId,
    currentLandId,
    isFirstDocumentLandVisit,
    navigate,
    requestedSection,
    updateRoute,
  ])

  const navigateToSection = useCallback((section) => {
    const safeSection = normalizeLandSection(section)

    if (currentLandId) {
      setLastLandSection(currentLandId, safeSection)
    }

    navigate(getSectionHref(safeSection))
  }, [currentLandId, getSectionHref, navigate])

  const goBackToField = useCallback(() => {
    if (fieldPath) {
      navigate(fieldPath)
    }
  }, [fieldPath, navigate])

  const goToUpdate = useCallback(() => {
    if (baseLandPath) {
      navigate(`${baseLandPath}/update`)
    }
  }, [baseLandPath, navigate])

  const value = {
    land,
    setLand,
    setLandId,
    initViewLand,
    loading,
    stopViewingLand,
    suspendLandRequests,
    resumeLandRequests,
    forgetDeletedLand,
    baseLandPath,
    fieldPath,
    activeSection,
    isRouteEditing: updateRoute != null,
    getSectionHref,
    navigateToSection,
    goBackToField,
    goToUpdate,
  }

  return <ViewLandContext.Provider value={value}>{children}</ViewLandContext.Provider>
}
